import uuid from 'utils/uuid';
import { isUrlValid } from 'utils/validator';
import { PLACEHOLDER_TYPE, PAGE_OPTION, ORIENTATION } from './constants';
import { getEffectiveStyle } from './effectiveStyles';
import { getDisplayPageSize, getPageSize, getBleedZone, getSafeZoneFrame } from './geometry';
import { mergeStyleOverBase } from './styleMerge';
import { hasPageOverlays } from './overlays';
import { getPageAssetUrl } from './assets';
import { getInitialPlaceholderData } from './placeholders';

/**
 * @param {object} template
 * @returns {string}
 */
export function getLayoutType(template) {
  return template.template.layout;
}

export function getTemplatePage(template, id, type = 'pages') {
  return template[type].find((p) => p.id === id);
}

export function hasPageLayers(page) {
  if (page.placeholders) {
    return page.placeholders.some((placeholder) => placeholder.layer);
  }
  return false;
}

/**
 * Transform page to common, more usable format with some usable data
 * @param {object} template
 * @param {object} page
 * @param {string} type
 * @param {object} options
 * @param {number} options.useServerAssets
 * @param {object} [options.customStyles] Draft custom text styles (template ⊕ draft merge)
 * @returns {object}
 */
export function getOriginalPage(template, page, type = 'pages', { useServerAssets, customStyles } = {}) {
  let originalPage = {};
  // When the requested page id is missing we fall back to another template page,
  // which overwrites page.id via the spread below. Surface why through the return
  // value (fallbackReason) instead of logging — this runs per page on every render
  // and in pdfer server logs, where an uncatchable console.warn is just noise.
  let fallbackReason;
  if (template[type]) {
    originalPage = getTemplatePage(template, page.id, type);

    if (!originalPage && template[type].length === 1) {
      originalPage = template[type][0];
      fallbackReason = 'sole-template-page';
    }

    if (!originalPage) {
      originalPage = template[type].find((p) => p.orientation === page.orientation);
      if (originalPage) {
        fallbackReason = 'orientation-match';
      }
    }

    if (!originalPage) {
      // TODO remove this and throw exception
      originalPage = template[type][0];
      fallbackReason = 'first-template-page';
    }
  } else if (type === 'back') {
    // when template has no back section, create empty back page
    originalPage.id = 'back';
  }

  // Assign style to page
  originalPage = {
    ...(getEffectiveStyle(template, customStyles, page.override?.styleName ?? originalPage.styleName) ?? {}),
    type: undefined,
    ...originalPage,
  };

  const isTOC = originalPage.optionId === PAGE_OPTION.TOC;
  const isIndex = originalPage.optionId === PAGE_OPTION.INDEX;

  let size = getPageSize(template, type);
  let useDisplaySize = false;
  if (!useServerAssets) {
    size = getDisplayPageSize(template, type) ?? size;
    useDisplaySize = true;
  }

  // DEFAULT STATE OF PAGE WITHOUT ORIENTATION
  let width = size[0];
  let height = size[1];

  if (originalPage.orientation === ORIENTATION.PORTRAIT) {
    width = Math.min(size[0], size[1]);
    height = Math.max(size[0], size[1]);
  }

  if (originalPage.orientation === ORIENTATION.LANDSCAPE) {
    width = Math.max(size[0], size[1]);
    height = Math.min(size[0], size[1]);
  }

  // BOOK
  if (type === 'pages' && originalPage.span === 1) {
    width *= 2;
  }

  // COVER
  let offsetX = 0;
  if (type === 'cover' && page.spine != null) {
    offsetX += page.spine;
    if (template.back) {
      const [backSizeWidth] = getPageSize(template, 'back');
      offsetX += backSizeWidth;
    }
    width += offsetX;
  }

  const bleed = getBleedZone(template, page, type);
  const safeFrame = getSafeZoneFrame(width, height, bleed);

  // placeholders defined in template
  // Apply each placeholder's named style over its base (style wins on conflict),
  // using the same canonical merge as the Polotno converter; page data overrides later.
  const templatePlaceholders = (originalPage.placeholders ?? []).map((placeholder) =>
    mergeStyleOverBase(placeholder, getEffectiveStyle(template, customStyles, placeholder.styleName) ?? {}),
  );

  // placeholders defined in data
  const pagePlaceholders = [...(page.placeholders ?? [])];
  if (page.withTemplatePlaceholders) {
    templatePlaceholders.forEach((templatePlaceholder) => {
      if (pagePlaceholders.find((p) => p.id === templatePlaceholder.id) == null) {
        pagePlaceholders.push({
          id: templatePlaceholder.id,
          type: templatePlaceholder.type,
          data: {},
        });
      }
    });
  }

  const placeholders = [];

  if (page.override?.backgroundImage) {
    const url = page.override.backgroundImage;
    if (isUrlValid(url)) {
      placeholders.push({
        id: 'background-placeholder',
        type: PLACEHOLDER_TYPE.PHOTO,
        data: {
          photo_url: url,
          x: 0.5,
          y: 0.5,
          scale: 1,
          orientation: 0,
        },
        override: {
          id: 'background-placeholder',
          type: PLACEHOLDER_TYPE.PHOTO,
          frame: [0, 0, width, height],
        },
      });
    }
  }

  // TODO hack, when type is back, automatically use template defined placeholders
  if (hasPageLayers(originalPage) || type === 'back') {
    // when page has layers, use ONLY templatePlaceholders
    templatePlaceholders.forEach((placeholder) => {
      const pagePlaceholder = pagePlaceholders.find((p) => p.id === placeholder.id);
      placeholders.push({ data: {}, ...placeholder, ...pagePlaceholder });
    });
  } else {
    // page.placeholders cointains many other placeholders (etc. for leather cover titles)
    // so we need to filter only usable placeholders (defined in template or with full representation)
    pagePlaceholders.forEach((placeholder) => {
      const templatePlaceholder = templatePlaceholders.find((p) => p.id === placeholder.id);
      if (templatePlaceholder) {
        placeholders.push({ data: {}, ...templatePlaceholder, ...placeholder });
      } else if (placeholder.override) {
        const override = { type: placeholder.type, ...placeholder.override };
        // add only valid placeholders with full representation
        if (override.id && override.type && (override.frame || override.points)) {
          placeholders.push({ data: {}, ...placeholder, override });
        }
      }
    });
    // spine text placeholder
    if (type === 'cover' && page.spine != null) {
      let spineIndex = placeholders.findIndex(
        (placeholder) => placeholder.id === 'cover_spine_text',
      );
      if (spineIndex < 0) {
        spineIndex =
          placeholders.push({
            id: 'cover_spine_text',
            type: 'text',
            data: {},
            override: {
              id: 'cover_spine_text',
              type: 'text',
              angle: -90,
              fontName: 'Baskerville-SemiBold',
              fontSize: 14,
              color: '#000000',
              alignment: 'center',
              verticalAlignment: 'center',
              label: 'SPINE TEXT',
            },
          }) - 1;
      }
      let spineY = height;
      let spineWidth = height;
      if (bleed != null) {
        spineY -= bleed[1];
        spineWidth -= bleed[1] + bleed[3];
      }
      placeholders[spineIndex].override.frame = [-page.spine, spineY, spineWidth, page.spine];
    }
  }

  // Reuse an existing key (native/editor pages and template pages carry one);
  // generate a uuid only as a fallback so the hot path never wastes work and the
  // fallback intent is explicit instead of hidden in spread order.
  const key = originalPage.key ?? page.key ?? uuid();

  return {
    ...page,
    ...originalPage,
    key,
    placeholders,
    type,
    size: { width, height, offsetX, useDisplaySize },
    bleed,
    safeFrame,
    isTOC,
    isIndex,
    fallbackReason,
  };
}

/**
 * Whether a resolved page (`getOriginalPage` output) renders nothing but its
 * background in PDFer, so the client can draw it instead (see `getEmptyPagePreview`).
 *
 * The auto-added `cover_spine_text` is not rendered while empty. Overlays and a
 * faded background (white overlay at `1 - backgroundOpacity`) keep the request.
 * Polotno saves `backgroundOpacity: 1` on every page, which has no visible effect.
 * @param {object} template
 * @param {object} originalPage
 * @returns {boolean}
 */
export function isOriginalPageEmpty(template, originalPage) {
  if (originalPage.type !== 'pages' && originalPage.type !== 'cover') {
    return false;
  }
  if (hasPageOverlays(originalPage) || (originalPage.override?.backgroundOpacity ?? 1) < 1) {
    return false;
  }

  const isCoverWithSpine = originalPage.type === 'cover' && originalPage.spine != null;
  // PDFer renders the template back page placeholders next to the spine.
  if (isCoverWithSpine && (template.back?.[0]?.placeholders?.length ?? 0) > 0) {
    return false;
  }

  return originalPage.placeholders.every(
    (placeholder) => isCoverWithSpine && placeholder.id === 'cover_spine_text' && !placeholder.data?.value,
  );
}

/**
 * Layers PDFer draws for an empty cover with spine (see `convertPageToPolotno`),
 * as fractions of the cover size so the client can position them with CSS.
 * @param {object} template
 * @param {object} originalPage Result of `getOriginalPage(template, page, 'cover')`
 * @returns {Array<{ id: string, src?: string, shadow?: 'left'|'right', x: number, y: number, width: number, height: number }>}
 */
export function getEmptyCoverLayers(template, originalPage) {
  const { width, height, offsetX } = originalPage.size;
  const [coverWidth, coverHeight] = getPageSize(template, 'cover');
  const layer = (id, x, w, h, rest) => ({ id, x: x / width, y: 0, width: w / width, height: h / height, ...rest });

  const layers = [];
  let spineX = 0;
  if (template.back) {
    const [backWidth, backHeight] = getPageSize(template, 'back');
    layers.push(layer('back', 0, backWidth, backHeight, { src: getPageAssetUrl(template, template.back[0].id) }));
    spineX = backWidth;
  }
  layers.push(
    layer('spine', spineX, originalPage.spine, coverHeight, {
      src: getPageAssetUrl(template, 'spine', { folder: '' }),
    }),
    layer('front', offsetX, coverWidth, coverHeight, { src: getPageAssetUrl(template, originalPage.id) }),
    // 1 cm shadows on both sides of the spine (generateSpineOverlay)
    layer('shadow-left', offsetX - originalPage.spine - 1, 1, height, { shadow: 'left' }),
    layer('shadow-right', offsetX, 1, height, { shadow: 'right' }),
  );
  return layers;
}

export function getTransformedPage(template, originalPage, newPageId, type = 'pages', customStyles) {
  const templatePage = getTemplatePage(template, newPageId, type);

  const placeholders = [];
  if (templatePage) {
    templatePage.placeholders.forEach((placeholder) => {
      const { data } = originalPage.placeholders.find((p) => p.id === placeholder.id) ?? {};

      placeholders.push({
        ...placeholder,
        data: { ...getInitialPlaceholderData(placeholder), ...data },
      });
    });
  }

  return getOriginalPage(
    template,
    {
      id: newPageId,
      placeholders,
    },
    type,
    { customStyles },
  );
}

export function getEmptyPage(template) {
  const size = getDisplayPageSize(template, 'pages') ?? getPageSize(template, 'pages');

  const width = size[0];
  const height = size[1];

  return { size: { width, height } };
}
